import { useEffect, useMemo, useState } from "react";
import {
  Bath,
  BriefcaseBusiness,
  CalendarDays,
  Car,
  Dog,
  Heart,
  Home,
  MapPin,
  Minus,
  PawPrint,
  Plus,
  Search,
  Sparkles,
  Stethoscope,
  type LucideIcon,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { type DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type MarketplaceInlineSearchValue = {
  service: string;
  city: string;
  startDate: string;
  endDate: string;
  petCount: string;
};

type MarketplaceInlineSearchBarProps = {
  value: MarketplaceInlineSearchValue;
  cities?: string[];
  onChange?: (value: MarketplaceInlineSearchValue) => void;
  onSubmit: (value: MarketplaceInlineSearchValue) => void;
  compact?: boolean;
  className?: string;
};

type SearchField = "service" | "city" | "dates" | "pets";

type ServiceOption = {
  label: string;
  slug: string;
  icon: LucideIcon;
};

type CanonicalCityItem = {
  label?: string;
  cidade?: string;
  estado?: string;
  uf?: string;
  regiao?: string;
};

type CanonicalCitiesPayload = {
  items?: CanonicalCityItem[];
  total?: number;
};

const services: ServiceOption[] = [
  { label: "Hospedagem", slug: "hospedagem", icon: Home },
  { label: "Creche", slug: "creche", icon: PawPrint },
  { label: "Pet sitter", slug: "petsitter", icon: Heart },
  { label: "Passeio", slug: "passeio", icon: Dog },
  { label: "Táxi dog", slug: "taxi_dog", icon: Car },
  { label: "Banho e tosa", slug: "banho_e_tosa", icon: Bath },
  { label: "Adestramento", slug: "adestramento", icon: Sparkles },
  { label: "Veterinário em casa", slug: "veterinario_casa", icon: Stethoscope },
];

function parseYmd(value?: string) {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

function toYmd(value?: Date) {
  if (!value) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function useDesktopCalendar() {
  const [desktop, setDesktop] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return desktop;
}

function canonicalCityLabel(item: CanonicalCityItem) {
  const label = String(item.label || "").trim();
  if (label) return label;

  const city = String(item.cidade || "").trim();
  const state = String(item.uf || item.estado || "").trim();

  if (!city) return "";
  return state ? `${city}, ${state}` : city;
}

function normalizeCityText(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("pt-BR");
}

function accentWeight(value: string) {
  return (value.normalize("NFD").match(/[\u0300-\u036f]/g) || []).length;
}

function preferredCityLabel(current: string | undefined, candidate: string) {
  if (!current) return candidate;

  const currentAccentWeight = accentWeight(current);
  const candidateAccentWeight = accentWeight(candidate);

  if (candidateAccentWeight !== currentAccentWeight) {
    return candidateAccentWeight > currentAccentWeight ? candidate : current;
  }

  return candidate.localeCompare(current, "pt-BR") < 0 ? candidate : current;
}

function normalizeCityOptions(values: string[]) {
  type CityEntry = { label: string; uf: string };

  const cityGroups = new Map<string, CityEntry[]>();
  const regions = new Map<string, string>();

  values.forEach((rawValue) => {
    const value = String(rawValue || "").trim();
    if (!value) return;

    const normalizedValue = normalizeCityText(value);

    if (normalizedValue.startsWith("sao paulo - ")) {
      const current = regions.get(normalizedValue);
      regions.set(normalizedValue, preferredCityLabel(current, value));
      return;
    }

    const match = value.match(/^(.*?),\s*([A-Za-z]{2})$/);
    const city = (match?.[1] || value).trim();
    const uf = String(match?.[2] || "").trim().toUpperCase();
    const cityKey = normalizeCityText(city);

    if (!cityKey) return;

    const entries = cityGroups.get(cityKey) || [];
    entries.push({ label: uf ? `${city}, ${uf}` : city, uf });
    cityGroups.set(cityKey, entries);
  });

  const normalizedCities: string[] = [];

  cityGroups.forEach((entries) => {
    const withState = entries.filter((entry) => entry.uf !== "");

    if (withState.length > 0) {
      const byState = new Map<string, string>();

      withState.forEach((entry) => {
        const current = byState.get(entry.uf);
        byState.set(entry.uf, preferredCityLabel(current, entry.label));
      });

      normalizedCities.push(...byState.values());
      return;
    }

    const preferred = entries.reduce<string | undefined>(
      (current, entry) => preferredCityLabel(current, entry.label),
      undefined,
    );

    if (preferred) normalizedCities.push(preferred);
  });

  return [...normalizedCities, ...regions.values()].sort((a, b) =>
    a.localeCompare(b, "pt-BR"),
  );
}

export default function MarketplaceInlineSearchBar({
  value,
  cities = [],
  onChange,
  onSubmit,
  compact = false,
  className = "",
}: MarketplaceInlineSearchBarProps) {
  const desktopCalendar = useDesktopCalendar();
  const [draft, setDraft] = useState<MarketplaceInlineSearchValue>(value);
  const [openField, setOpenField] = useState<SearchField | null>(null);
  const [cityQuery, setCityQuery] = useState("");
  const [canonicalCities, setCanonicalCities] = useState<string[]>([]);
  const [canonicalCitiesLoading, setCanonicalCitiesLoading] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>({
    from: parseYmd(value.startDate),
    to: parseYmd(value.endDate),
  });

  useEffect(() => {
    setDraft(value);
    setRange({
      from: parseYmd(value.startDate),
      to: parseYmd(value.endDate),
    });
  }, [value.service, value.city, value.startDate, value.endDate, value.petCount]);

  const updateDraft = (patch: Partial<MarketplaceInlineSearchValue>) => {
    setDraft((current) => {
      const next = { ...current, ...patch };
      onChange?.(next);
      return next;
    });
  };

  const refreshCanonicalCities = async () => {
    setCanonicalCitiesLoading(true);

    try {
      const response = await fetch(`/api/buscar/cidades?ts=${Date.now()}`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = (await response.json()) as CanonicalCitiesPayload;
      const labels = (payload.items || [])
        .map(canonicalCityLabel)
        .filter(Boolean);

      setCanonicalCities(labels);
    } catch {
      // Mantem as cidades recebidas pela pagina como fallback se a API canonica falhar.
      setCanonicalCities([]);
    } finally {
      setCanonicalCitiesLoading(false);
    }
  };

  const allCities = useMemo(
    () => normalizeCityOptions([...canonicalCities, ...cities]),
    [canonicalCities, cities],
  );

  const filteredCities = useMemo(() => {
    const query = cityQuery.trim().toLocaleLowerCase("pt-BR");
    if (!query) return allCities;

    return allCities.filter((city) =>
      city.toLocaleLowerCase("pt-BR").includes(query),
    );
  }, [allCities, cityQuery]);

  const selectedService = services.find((item) => item.slug === draft.service);
  const showTodayShortcut = draft.service !== "hospedagem";
  const dateLabel = range?.from
    ? range.to
      ? `${format(range.from, "dd MMM", { locale: ptBR })} – ${format(range.to, "dd MMM", { locale: ptBR })}`
      : format(range.from, "dd MMM", { locale: ptBR })
    : "Adicionar datas";

  const chooseToday = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    setRange({ from: today, to: today });
    updateDraft({ startDate: toYmd(today), endDate: toYmd(today) });
    setOpenField(null);
  };

  const setFieldOpen = (field: SearchField, open: boolean) => {
    setOpenField(open ? field : null);

    if (field === "city" && open) {
      setCityQuery(draft.city || "");
      void refreshCanonicalCities();
    }
  };

  const handleDateSelect = (next?: DateRange) => {
    setRange(next);
    updateDraft({
      startDate: toYmd(next?.from),
      endDate: toYmd(next?.to),
    });
  };

  const submit = () => {
    setOpenField(null);
    onSubmit({
      ...draft,
      endDate: draft.endDate || draft.startDate,
      petCount: draft.petCount || "1",
    });
  };

  const segmentClass = (field: SearchField) =>
    `min-w-0 rounded-2xl px-4 text-left transition md:rounded-full ${
      compact ? "py-2.5" : "py-3"
    } ${openField === field ? "bg-slate-100 shadow-inner" : "hover:bg-slate-50"}`;

  return (
    <div className={`w-full ${className}`}>
      <div className="rounded-[28px] border border-slate-200 bg-white p-2 shadow-[0_12px_40px_rgba(15,23,42,0.10)] md:rounded-full">
        <div className="grid grid-cols-2 gap-1 md:flex md:items-center md:gap-0">
          <Popover
            open={openField === "service"}
            onOpenChange={(open) => setFieldOpen("service", open)}
          >
            <PopoverTrigger asChild>
              <button type="button" className={`${segmentClass("service")} md:flex-1 md:border-r md:border-slate-200`}>
                <span className="block text-xs font-black text-slate-950">Serviço</span>
                <span className="mt-0.5 flex min-w-0 items-center gap-2 text-sm text-slate-500">
                  <BriefcaseBusiness className="h-4 w-4 shrink-0" />
                  <span className="truncate">{selectedService?.label || "Escolha o serviço"}</span>
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={10}
              collisionPadding={16}
              className="w-[min(92vw,560px)] rounded-[24px] border-slate-200 bg-white p-4 shadow-2xl"
            >
              <p className="mb-3 text-sm font-black text-slate-950">Qual serviço seu pet precisa?</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {services.map(({ label, slug, icon: Icon }) => (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => {
                      updateDraft({ service: slug });
                      setOpenField(null);
                    }}
                    className={`flex min-h-[88px] flex-col items-start justify-between rounded-2xl border p-3 text-left transition ${
                      draft.service === slug
                        ? "border-[#FE5668] bg-[#FE5668]/10 text-[#FE5668]"
                        : "border-slate-200 text-slate-700 hover:border-slate-400"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs font-bold leading-tight">{label}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Popover
            open={openField === "city"}
            onOpenChange={(open) => setFieldOpen("city", open)}
          >
            <PopoverTrigger asChild>
              <button type="button" className={`${segmentClass("city")} md:flex-1 md:border-r md:border-slate-200`}>
                <span className="block text-xs font-black text-slate-950">Onde</span>
                <span className="mt-0.5 flex min-w-0 items-center gap-2 text-sm text-slate-500">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span className="truncate">{draft.city || "Cidade ou bairro"}</span>
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={10}
              collisionPadding={16}
              className="w-[min(92vw,380px)] rounded-[24px] border-slate-200 bg-white p-4 shadow-2xl"
            >
              <p className="mb-3 text-sm font-black text-slate-950">Onde você precisa do serviço?</p>
              <label className="flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2.5 focus-within:border-[#FE5668] focus-within:ring-4 focus-within:ring-[#FE5668]/10">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  autoFocus
                  value={cityQuery}
                  onChange={(event) => {
                    setCityQuery(event.target.value);
                    updateDraft({ city: event.target.value });
                  }}
                  placeholder="Digite a cidade"
                  className="w-full bg-transparent text-sm outline-none"
                />
              </label>
              <div className="mt-3 max-h-64 overflow-y-auto">
                {canonicalCitiesLoading && allCities.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-500">Carregando cidades...</div>
                ) : filteredCities.length > 0 ? (
                  filteredCities.map((city) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => {
                        updateDraft({ city });
                        setCityQuery(city);
                        setOpenField(null);
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <MapPin className="h-4 w-4 text-[#64A002]" />
                      {city}
                    </button>
                  ))
                ) : (
                  <button
                    type="button"
                    onClick={() => setOpenField(null)}
                    className="w-full rounded-xl bg-slate-50 px-3 py-3 text-left text-sm text-slate-600"
                  >
                    Usar “{cityQuery}”
                  </button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <div className="flex min-w-0 items-center md:flex-[1.25] md:border-r md:border-slate-200">
          <Popover
            open={openField === "dates"}
            onOpenChange={(open) => setFieldOpen("dates", open)}
          >
            <PopoverTrigger asChild>
              <button type="button" className={`${segmentClass("dates")} min-w-0 flex-1`}>
                <span className="block text-xs font-black text-slate-950">Quando</span>
                <span className="mt-0.5 flex min-w-0 items-center gap-2 text-sm text-slate-500">
                  <CalendarDays className="h-4 w-4 shrink-0" />
                  <span className="truncate">{dateLabel}</span>
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              sideOffset={2}
              collisionPadding={{ top: 24, right: 16, bottom: 16, left: 16 }}
              className="w-[min(94vw,760px)] rounded-[28px] border-slate-200 bg-white p-3 shadow-2xl md:p-5"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-black text-slate-950">Escolha entrada e saída</p>
                  <p className="text-xs text-slate-500">Selecione o período diretamente no calendário.</p>
                </div>
                {range?.from && (
                  <button
                    type="button"
                    onClick={() => handleDateSelect(undefined)}
                    className="text-xs font-bold text-[#FE5668]"
                  >
                    Limpar
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <Calendar
                  mode="range"
                  selected={range}
                  onSelect={handleDateSelect}
                  numberOfMonths={desktopCalendar ? 2 : 1}
                  locale={ptBR}
                  weekStartsOn={0}
                  disabled={{
                    before: new Date(
                      new Date().getFullYear(),
                      new Date().getMonth(),
                      new Date().getDate()
                    ),
                  }}
                  className="mx-auto"
                  classNames={{
                    day_selected:
                      "bg-[#FE5668] text-white hover:bg-[#FE5668] hover:text-white focus:bg-[#FE5668] focus:text-white",
                    day_range_middle:
                      "aria-selected:bg-[#FE5668]/10 aria-selected:text-slate-950",
                    day_today: "font-black text-[#64A002]",
                  }}
                />
              </div>
            </PopoverContent>
          </Popover>
          {showTodayShortcut && (
            <button
              type="button"
              onClick={chooseToday}
              aria-label="Selecionar atendimento hoje"
              className="mr-2 shrink-0 rounded-full border border-[#FE5668] px-3 py-1.5 text-xs font-bold text-[#FE5668] transition hover:bg-[#FE5668] hover:text-white"
            >
              Hoje
            </button>
          )}
          </div>

          <Popover
            open={openField === "pets"}
            onOpenChange={(open) => setFieldOpen("pets", open)}
          >
            <PopoverTrigger asChild>
              <button type="button" className={`${segmentClass("pets")} md:w-[150px]`}>
                <span className="block text-xs font-black text-slate-950">Pets</span>
                <span className="mt-0.5 flex min-w-0 items-center gap-2 text-sm text-slate-500">
                  <PawPrint className="h-4 w-4 shrink-0" />
                  <span className="truncate">{draft.petCount || "1"} pet{draft.petCount === "1" ? "" : "s"}</span>
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={10}
              collisionPadding={16}
              className="w-[min(92vw,340px)] rounded-[24px] border-slate-200 bg-white p-5 shadow-2xl"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-black text-slate-950">Quantidade de pets</p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">Usada para verificar capacidade e disponibilidade.</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={Number(draft.petCount || 1) <= 1}
                    onClick={() =>
                      updateDraft({
                        petCount: String(Math.max(1, Number(draft.petCount || 1) - 1)),
                      })
                    }
                    className="grid h-9 w-9 place-items-center rounded-full border border-slate-300 disabled:opacity-30"
                    aria-label="Diminuir quantidade de pets"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <strong className="min-w-5 text-center">{draft.petCount || "1"}</strong>
                  <button
                    type="button"
                    disabled={Number(draft.petCount || 1) >= 5}
                    onClick={() =>
                      updateDraft({
                        petCount: String(Math.min(5, Number(draft.petCount || 1) + 1)),
                      })
                    }
                    className="grid h-9 w-9 place-items-center rounded-full border border-slate-300 disabled:opacity-30"
                    aria-label="Aumentar quantidade de pets"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <button
            type="button"
            onClick={submit}
            className={`col-span-2 flex items-center justify-center gap-2 rounded-2xl bg-[#FE5668] font-black text-white shadow-lg shadow-[#FE5668]/20 transition hover:bg-[#ef4559] md:col-span-1 md:ml-2 md:rounded-full ${
              compact ? "px-5 py-3" : "px-6 py-4 md:h-12"
            }`}
          >
            <Search className="h-5 w-5" />
            <span className={compact ? "hidden xl:inline" : "md:hidden lg:inline"}>Buscar</span>
          </button>
        </div>
      </div>
    </div>
  );
}
